import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react"

import {
  useFormContext,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from "react-hook-form"

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import type { EmbedTurnstileFrameConfig } from "@/embed/turnstile-frame-context"
import {
  loadTurnstile,
  type TurnstileAction,
  type TurnstileApi,
  type TurnstileAppearance,
} from "@/lib/turnstile"
import {
  isTurnstileFrameMessage,
  TURNSTILE_FRAME_INIT,
  type TurnstileFrameCommand,
  type TurnstileFrameInitMessage,
} from "@/lib/turnstile-frame-protocol"

const INITIAL_FRAME_HEIGHT = 80
const MIN_FRAME_HEIGHT = 65
const MAX_FRAME_HEIGHT = 500
const FRAME_READY_TIMEOUT_MS = 15_000
const LOAD_ERROR_MESSAGE =
  "Unable to load the verification challenge. Please try again."

type FrameSession = {
  iframe: HTMLIFrameElement
  port: MessagePort
}

function withFrameSession(frameUrl: string, sessionId: string): string {
  const url = new URL(frameUrl)
  url.hash = new URLSearchParams({ session: sessionId }).toString()
  return url.toString()
}

export type FormTurnstileHandle = {
  /** Invalidate the current token and ask the active renderer for a new one. */
  reset: () => void
}

// The isolated embed frame owns its site key and remains signup-only. Direct
// widgets can select any action understood by the backend verifier.
type FormTurnstileRendererProps =
  | {
      frame: EmbedTurnstileFrameConfig
      siteKey?: never
      action: "signup"
      appearance?: never
    }
  | {
      frame?: never
      siteKey: string
      action: TurnstileAction
      /** `interaction-only` keeps the widget hidden until a challenge needs the user. */
      appearance?: TurnstileAppearance
    }

export type FormTurnstileProps<T extends FieldValues = FieldValues> =
  FormTurnstileRendererProps & {
    name: FieldPath<T>
    className?: string
  }

/**
 * Connects React Hook Form to either a direct Turnstile widget or the isolated
 * embed frame while keeping their token, error, and reset lifecycles identical.
 */
function FormTurnstileComponent<T extends FieldValues = FieldValues>(
  {
    name,
    siteKey,
    action,
    appearance = "always",
    className,
    frame,
  }: FormTurnstileProps<T>,
  ref: React.ForwardedRef<FormTurnstileHandle>,
) {
  const { clearErrors, control, setError, setValue } = useFormContext<T>()
  const internalTargetRef = useRef<HTMLDivElement>(null)
  const apiRef = useRef<TurnstileApi>(null)
  const widgetIdRef = useRef<string>(null)
  const frameSessionRef = useRef<FrameSession>(null)
  const frameSessionIdRef = useRef<string>(null)
  const frameReadyTimeoutRef = useRef<number>(null)

  if (frame && !frameSessionIdRef.current) {
    frameSessionIdRef.current = crypto.randomUUID()
  }

  const frameSessionId = frameSessionIdRef.current
  const frameSrc =
    frame && frameSessionId
      ? withFrameSession(frame.frameUrl, frameSessionId)
      : undefined

  const clearCaptcha = useCallback(() => {
    setValue(name, "" as FieldPathValue<T, typeof name>, {
      shouldValidate: true,
    })
  }, [name, setValue])

  const closeFrameSession = useCallback(() => {
    const session = frameSessionRef.current
    frameSessionRef.current = null
    if (!session) return

    session.port.onmessage = null
    session.port.close()
  }, [])

  const clearFrameReadyTimeout = useCallback(() => {
    if (frameReadyTimeoutRef.current === null) return
    window.clearTimeout(frameReadyTimeoutRef.current)
    frameReadyTimeoutRef.current = null
  }, [])

  const setLoadError = useCallback(
    (message = LOAD_ERROR_MESSAGE) => {
      clearCaptcha()
      setError(name, { type: "turnstile", message })
    },
    [clearCaptcha, name, setError],
  )

  const armFrameReadyTimeout = useCallback(() => {
    clearFrameReadyTimeout()
    frameReadyTimeoutRef.current = window.setTimeout(() => {
      frameReadyTimeoutRef.current = null
      closeFrameSession()
      setLoadError()
    }, FRAME_READY_TIMEOUT_MS)
  }, [clearFrameReadyTimeout, closeFrameSession, setLoadError])

  // Covers a frame document that never fires its load event; each load event
  // re-arms the window for its own handshake in handleFrameLoad.
  useEffect(() => {
    if (!frameSrc) return

    armFrameReadyTimeout()
    return clearFrameReadyTimeout
  }, [armFrameReadyTimeout, clearFrameReadyTimeout, frameSrc])

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        const session = frameSessionRef.current
        if (session) {
          session.port.postMessage({
            type: "reset",
          } satisfies TurnstileFrameCommand)
        }
        if (apiRef.current && widgetIdRef.current) {
          apiRef.current.reset(widgetIdRef.current)
        }
        clearCaptcha()
      },
    }),
    [clearCaptcha],
  )

  const handleFrameLoad = useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>) => {
      if (!frame || !frameSessionId) return

      armFrameReadyTimeout()
      closeFrameSession()
      clearCaptcha()
      clearErrors(name)

      const iframe = event.currentTarget
      const frameWindow = iframe.contentWindow
      if (!frameWindow) {
        clearFrameReadyTimeout()
        setLoadError()
        return
      }

      let frameOrigin: string
      try {
        frameOrigin = new URL(frame.frameUrl).origin
      } catch (error: unknown) {
        clearFrameReadyTimeout()
        setLoadError()
        console.error(error)
        return
      }

      const channel = new MessageChannel()
      const session: FrameSession = { iframe, port: channel.port1 }
      frameSessionRef.current = session

      channel.port1.onmessage = (messageEvent: MessageEvent<unknown>) => {
        if (
          frameSessionRef.current !== session ||
          !isTurnstileFrameMessage(messageEvent.data)
        ) {
          return
        }

        const message = messageEvent.data
        switch (message.type) {
          case "ready":
            clearFrameReadyTimeout()
            return
          case "token":
            clearErrors(name)
            setValue(name, message.token as FieldPathValue<T, typeof name>, {
              shouldValidate: true,
            })
            return
          case "invalidated":
            clearCaptcha()
            return
          case "fatal":
            clearFrameReadyTimeout()
            closeFrameSession()
            setLoadError(message.message)
            return
          case "resize": {
            const height = Math.min(
              MAX_FRAME_HEIGHT,
              Math.max(MIN_FRAME_HEIGHT, Math.ceil(message.height)),
            )
            session.iframe.style.height = `${height}px`
            return
          }
        }
      }
      channel.port1.start()

      const initMessage: TurnstileFrameInitMessage = {
        type: TURNSTILE_FRAME_INIT,
        sessionId: frameSessionId,
      }
      try {
        frameWindow.postMessage(initMessage, frameOrigin, [channel.port2])
      } catch (error: unknown) {
        clearFrameReadyTimeout()
        channel.port2.close()
        closeFrameSession()
        setLoadError()
        console.error(error)
      }
    },
    [
      armFrameReadyTimeout,
      clearCaptcha,
      clearErrors,
      clearFrameReadyTimeout,
      closeFrameSession,
      frame,
      frameSessionId,
      name,
      setLoadError,
      setValue,
    ],
  )

  useEffect(() => closeFrameSession, [closeFrameSession, frame?.frameUrl])

  useEffect(() => {
    if (frame) return

    const widgetTarget = internalTargetRef.current
    if (!widgetTarget) return

    let active = true

    void loadTurnstile()
      .then((turnstile) => {
        if (!active) return

        apiRef.current = turnstile
        widgetIdRef.current = turnstile.render(widgetTarget, {
          sitekey: siteKey,
          action,
          theme: "light",
          size: "flexible",
          appearance,
          "response-field": false,
          callback: (token) => {
            clearErrors(name)
            setValue(name, token as FieldPathValue<T, typeof name>, {
              shouldValidate: true,
            })
          },
          "error-callback": clearCaptcha,
          "expired-callback": clearCaptcha,
          "timeout-callback": clearCaptcha,
        })
      })
      .catch((error: unknown) => {
        if (!active) return
        clearCaptcha()
        setError(name, {
          type: "turnstile",
          message: LOAD_ERROR_MESSAGE,
        })
        console.error(error)
      })

    return () => {
      active = false
      if (apiRef.current && widgetIdRef.current) {
        apiRef.current.remove(widgetIdRef.current)
      }
      apiRef.current = null
      widgetIdRef.current = null
    }
  }, [
    action,
    appearance,
    clearCaptcha,
    clearErrors,
    frame,
    name,
    setError,
    setValue,
    siteKey,
  ])

  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem className={className}>
          <FormControl>
            <div className="w-full">
              {frame ? (
                <iframe
                  key={frameSrc}
                  src={frameSrc}
                  title="Security verification challenge"
                  onLoad={handleFrameLoad}
                  style={{
                    border: 0,
                    display: "block",
                    height: `${INITIAL_FRAME_HEIGHT}px`,
                    width: "100%",
                  }}
                />
              ) : (
                <div ref={internalTargetRef} className="w-full" />
              )}
            </div>
          </FormControl>
          <FormMessage className="form-error" />
        </FormItem>
      )}
    />
  )
}

export const FormTurnstile = forwardRef(FormTurnstileComponent) as <
  T extends FieldValues = FieldValues,
>(
  props: FormTurnstileProps<T> & React.RefAttributes<FormTurnstileHandle>,
) => React.ReactElement
